import * as React from 'react'

import { renderers } from '@markdoc/markdoc'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, redirect, useLoaderData } from 'react-router'

import ContentImage from '@/components/content/ContentImage'
import Logo from '@/components/Logo'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import YouTube from '@/components/YouTube'
import { findCaseStudy } from '@/models/case-study.server'
import { pageHelper } from '@/request'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const caseStudy = findCaseStudy(params.slug as string)

  if (!caseStudy) {
    throw new Response('Not Found', { status: 404 })
  }

  if (caseStudy.external && caseStudy.url) {
    throw redirect(caseStudy.url, 301)
  }

  const { root } = pageHelper(url)

  return {
    caseStudy: caseStudy,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const {
    caseStudy: { meta },
    root
  } = loaderData

  const title = `${meta.title} — PlanetScale`
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: meta.description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: meta.canonical },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: meta.description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: meta.canonical },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: meta.description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: meta.canonical }
  ]
}

export default function CaseStudy() {
  const { caseStudy } = useLoaderData<LoaderData>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <div className='flex flex-col gap-y-3 lg:flex-row-reverse lg:gap-x-6'>
          {caseStudy.aside && (
            <aside className='w-full shrink-0 lg:w-36'>
              {caseStudy.logo && (
                <Logo
                  name={caseStudy.logo}
                  ariaLabel={caseStudy.name}
                  className='block h-[intrinsic] max-h-20 w-auto max-w-24 dark:hidden'
                />
              )}
              {caseStudy.logoDarkmode && (
                <Logo
                  name={caseStudy.logoDarkmode}
                  ariaLabel={caseStudy.name}
                  className='hidden h-[intrinsic] max-h-20 w-auto max-w-24 dark:block'
                />
              )}

              <section className='mt-3 hidden lg:block'>
                {renderers.react(caseStudy.aside, React, {
                  components: {
                    ContentImage
                  }
                })}
              </section>
            </aside>
          )}

          <article className='hr-dot-fill'>
            {renderers.react(caseStudy.body, React, {
              components: {
                ContentImage,
                YouTube
              }
            })}

            <Link className='font-semibold' prefetch='intent' to='/case-studies'>
              ← Back to all case studies
            </Link>

            <div className='mt-6 border p-3'>
              <h3>Your business deserves a predictable database</h3>

              <p>
                Never worry about another 3am wake-up call saying the site is down. Give your engineers the power they
                deserve with a PlanetScale database today.
              </p>

              <section className='cta'>
                <p>
                  <a href='https://auth.planetscale.com/sign-up'>Get started</a>{' '}
                  <Link prefetch='intent' to='/contact'>
                    Contact sales
                  </Link>
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>

      <SiteFooter />
    </>
  )
}
